// =====================================================================================
// INVRT Radar — web console — account — Ledger tab
//
// Every credit movement in the window. Moved verbatim out of AccountPage.tsx in the C1
// split; the page shell registers it under the tab id "ledger".
//
// Every figure here is one Postgres computed and the API handed over — the window totals
// come from the response's own `totals` block, never from re-adding the rows on screen.
// The CSV download is the SERVER's own (format=csv), for the same reason: this is the
// cost-basis record, so the file must be the figures the database produced.
// =====================================================================================

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { downloadTextFile, timestampedFilename } from "@/lib/csv";
import { formatCredits, formatDateTime, humaniseAction } from "@/lib/format";
import type { LedgerExportResponse } from "@/lib/types";

export function LedgerPanel() {
  const api = useApiClient();
  const { orgId } = useSession();
  const { push } = useToast();
  const [downloading, setDownloading] = useState(false);

  const ledger = useQuery({
    queryKey: ["ledger", orgId],
    queryFn: () => api.get<LedgerExportResponse>("/ledger/export", { query: { limit: 100 } }),
    enabled: Boolean(orgId),
  });

  async function downloadCsv(): Promise<void> {
    setDownloading(true);
    try {
      // The server's own CSV, not a client re-render of the table: these are the
      // cost-basis figures and they must be the ones Postgres produced.
      const csv = await api.get<string>("/ledger/export", {
        query: { format: "csv", limit: 200 },
        responseType: "text",
      });
      downloadTextFile(timestampedFilename("radar-ledger"), csv);
    } catch {
      push({
        tone: "danger",
        title: "The download did not complete",
        body: "Try again — nothing about your ledger changed.",
      });
    } finally {
      setDownloading(false);
    }
  }

  if (ledger.isLoading) return <LoadingState label="Loading your ledger…" />;
  if (ledger.isError) return <ErrorState error={ledger.error} onRetry={() => void ledger.refetch()} />;
  if (!ledger.data) return null;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Every credit movement</CardTitle>
            <p className="mt-1 text-sm text-ink-muted">
              {formatDateTime(ledger.data.window.from)} to {formatDateTime(ledger.data.window.to)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="info">
              Net this period {formatCredits(ledger.data.totals.window_credits_delta)}
            </Badge>
            <Badge tone="success">
              Balance {formatCredits(ledger.data.totals.balance_as_of)}
            </Badge>
            <Button
              variant="secondary"
              size="sm"
              loading={downloading}
              onClick={() => void downloadCsv()}
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              Download CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {ledger.data.rows.length === 0 ? (
            <EmptyState
              title="No movements in this period"
              body="Credits appear here the moment anything is granted, purchased or spent."
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>When</TableHeaderCell>
                  <TableHeaderCell>Action</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell className="text-right">Credits</TableHeaderCell>
                  <TableHeaderCell className="text-right">Units</TableHeaderCell>
                  <TableHeaderCell>Note</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledger.data.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(row.created_at)}
                    </TableCell>
                    <TableCell className="font-medium">{humaniseAction(row.action)}</TableCell>
                    <TableCell>
                      <Badge tone={row.entry_type === "burn" ? "warning" : "success"}>
                        {humaniseAction(row.entry_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCredits(row.credits_delta)}
                    </TableCell>
                    <TableCell className="text-right text-ink-muted">
                      {row.unit_count ?? "—"}
                    </TableCell>
                    <TableCell className="text-ink-muted">{row.memo ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {ledger.data.has_more ? (
            <p className="pt-3 text-xs text-ink-muted">
              Showing the most recent 100 movements. Download the CSV for the full period.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
